import { Injectable, NgZone } from "@angular/core";
import { Entity, ScreenSpaceEventHandler, ScreenSpaceEventType } from "cesium";
import { Observable, Subject, merge } from "rxjs";
import { filter, map, mergeMap, takeUntil, tap } from "rxjs/operators";
import { PickConfiguration } from "../../models/event-registration-input";
import { PickOptions } from "../../models/pickOptions.enum";
import { CesiumMapService } from "../cesium-map.service";
import { DisposableAndPausableObservable, DtmCesiumLongPressEvents, DtmEventRegistrationInput } from "./cesium-events.model";
import { DtmCesiumDragDropHelper } from "./dtm-cesium-drag-drop-helper";
import { ThrottledCesiumEventBuilder } from "./throttled-cesium-event-builder";

const UtilsService = {
    unique: (array: any[]): any[] =>
        array.reduce((accumulator, currentValue) => {
            if (accumulator.indexOf(currentValue) < 0) {
                accumulator.push(currentValue);
            }

            return accumulator;
        }, []),
};

/* eslint-disable @typescript-eslint/no-explicit-any */

class Registration {
    constructor(
        public observable: Observable<EventResult> | undefined,
        public stopper: Subject<any>,
        public priority: number = 0,
        public isPaused: boolean
    ) {}
}

/**
 * Returns screen position, drag boolean for drag events only
 */
export interface Movement {
    startPosition: { x: number; y: number };
    endPosition: { x: number; y: number };
    drop?: boolean;
}

export interface EventResult {
    movement: Movement;
    cesiumEntities: any[];
    entities: any[];
}

interface PickResult {
    movement: ScreenSpaceEventHandler.MotionEvent;
    cesiumEntities: any;
}

const DRILL_PICK_THROTTLE_TIME = 40;

/**
 * Extracted and rebuilt from angular-cesium
 *
 * __usage:__
 * ```
 * MapEventsManagerService.register({event, modifier, priority, entityType, pickOption}).subscribe()
 * ```
 * __param:__ {CesiumEvent} event
 * __param:__ {CesiumEventModifier} modifier
 * __param:__ priority - the bigger the number the bigger the priority. default : 0.
 * __param:__ entityType - entity type class that you are interested like (Track). the class must extends AcEntity
 * __param:__ pickOption - self explained
 */
@Injectable()
export class DtmMapEventsManagerService {
    private scene: any;
    private eventRegistrations = new Map<string, Registration[]>();
    private isDragging = false;

    private readonly drillPicksCache: {
        [key in PickOptions]?: {
            validityTime: number;
            value: [];
        };
    } = {};

    constructor(private cesiumService: CesiumMapService, private eventBuilder: ThrottledCesiumEventBuilder, private ngZone: NgZone) {
        this.init();
    }

    public init() {
        this.eventBuilder.init();
        this.scene = this.cesiumService.getScene();
    }

    private counter = 0;

    /**
     * Register to map event
     * @param input Event Registration Input
     *
     * @returns DisposableObservable<EventResult>
     */
    public register(input: DtmEventRegistrationInput): DisposableAndPausableObservable<EventResult> {
        if (this.scene === undefined) {
            throw new Error("CesiumService has not been initialized yet - MapEventsManagerService must be injected  under ac-map");
        }

        input.pick = input.pick || PickOptions.NO_PICK;
        input.priority = input.priority || 0;
        input.pickConfig = input.pickConfig || {};

        if (input.entityType && input.pick === PickOptions.NO_PICK) {
            throw new Error(
                "MapEventsManagerService: can't register an event " + "with entityType and PickOptions.NO_PICK - It doesn't make sense "
            );
        }

        const eventName = ThrottledCesiumEventBuilder.getEventFullName(input.event, input.modifier);

        if (!this.eventRegistrations.has(eventName)) {
            this.eventRegistrations.set(eventName, []);
        }

        const eventRegistration = this.createEventRegistration(input);
        const registrationObservable = eventRegistration.observable as DisposableAndPausableObservable<EventResult>;
        registrationObservable.dispose = () => this.disposeObservable(eventRegistration, eventName);
        this.eventRegistrations.get(eventName)?.push(eventRegistration);

        this.sortRegistrationsByPriority(eventName);

        return registrationObservable as DisposableAndPausableObservable<EventResult>;
    }

    private disposeObservable(eventRegistration: Registration, eventName: string) {
        eventRegistration.stopper.next(1);
        eventRegistration.stopper.complete();
        const registrations = this.eventRegistrations.get(eventName);
        const index = registrations?.indexOf(eventRegistration);
        if (index && index !== -1) {
            registrations?.splice(index, 1);
        }
        this.sortRegistrationsByPriority(eventName);
    }

    private sortRegistrationsByPriority(eventName: string) {
        const registrations = this.eventRegistrations.get(eventName);
        registrations?.sort((a, b) => b.priority - a.priority);
        if (!registrations || registrations?.length === 0) {
            return;
        }

        // Active registrations by priority
        const currentPriority = registrations[0].priority;
        registrations?.forEach((registration) => {
            registration.isPaused = registration.priority < currentPriority;
        });
    }

    private createEventRegistration({
        event,
        modifier,
        entityType,
        pick: pickOption,
        priority,
        pickFilter,
        pickConfig,
    }: DtmEventRegistrationInput): Registration {
        const cesiumEventObservable = this.ngZone.runOutsideAngular(() => this.eventBuilder.get(event, modifier));

        if (!cesiumEventObservable) {
            throw new Error("cesium event was not provided");
        }
        const stopper = new Subject<any>();

        const registration = new Registration(undefined, stopper, priority, false);
        let observable: Observable<EventResult>;

        if (!DtmCesiumDragDropHelper.dragEvents.has(event as DtmCesiumLongPressEvents)) {
            // build the high-frequency event pipeline outside Angular
            observable = this.ngZone.runOutsideAngular(() =>
                cesiumEventObservable.pipe(
                    filter(
                        () =>
                            !(registration.observable as DisposableAndPausableObservable<EventResult>).isPaused &&
                            !registration.isPaused &&
                            !(this.isDragging && event === ScreenSpaceEventType.MOUSE_MOVE)
                    ),
                    map((movement) => this.triggerPick(movement, pickOption, pickConfig)),
                    filter((result) => result.cesiumEntities !== null || entityType === undefined),
                    map((picksAndMovement) => this.addEntities(picksAndMovement, entityType, pickOption, pickFilter)),
                    filter((result) => result.entities !== null || (entityType === undefined && !pickFilter)),
                    takeUntil(stopper)
                )
            );
        } else {
            observable = this.createDragEvent({
                event: event as DtmCesiumLongPressEvents,
                modifier,
                entityType,
                pick: pickOption,
                priority,
                pickFilter,
                pickConfig,
            }).pipe(takeUntil(stopper));
        }

        registration.observable = observable;

        return registration;
    }

    private createDragEvent({
        event,
        modifier,
        entityType,
        pick: pickOption,
        priority,
        pickFilter,
        pickConfig,
    }: DtmEventRegistrationInput<DtmCesiumLongPressEvents>): Observable<EventResult> {
        const { mouseDownEvent, mouseUpEvent } = DtmCesiumDragDropHelper.getDragEventTypes(event);

        const mouseUpObservable = this.ngZone.runOutsideAngular(() => this.eventBuilder.get(mouseUpEvent));
        const mouseMoveObservable = this.ngZone.runOutsideAngular(() => this.eventBuilder.get(ScreenSpaceEventType.MOUSE_MOVE));

        const mouseDownRegistration = this.createEventRegistration({
            event: mouseDownEvent,
            modifier,
            entityType,
            pick: pickOption,
            priority,
            pickFilter,
            pickConfig,
        });

        const dropSubject = new Subject<EventResult>();
        const dragObserver = mouseDownRegistration.observable?.pipe(
            mergeMap((eventResult) => {
                this.isDragging = true;
                let lastMove: any = null;
                const dragStartPositionX = eventResult.movement.startPosition.x;
                const dragStartPositionY = eventResult.movement.startPosition.y;

                return mouseMoveObservable.pipe(
                    map((movement) => {
                        lastMove = {
                            movement: {
                                drop: false,
                                startPosition: {
                                    x: dragStartPositionX,
                                    y: dragStartPositionY,
                                },
                                endPosition: movement.endPosition,
                            },
                            entities: eventResult.entities,
                            cesiumEntities: eventResult.cesiumEntities,
                        };

                        return lastMove;
                    }),
                    // eslint-disable-next-line rxjs/no-unsafe-takeuntil
                    takeUntil(mouseUpObservable),
                    tap({
                        complete: () => {
                            this.isDragging = false;
                            // On complete
                            if (lastMove) {
                                const dropEvent = Object.assign({}, lastMove);
                                dropEvent.movement.drop = true;
                                dropSubject.next(dropEvent);
                            }
                        },
                    })
                );
            })
        );

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return merge(dragObserver!, dropSubject);
    }

    private triggerPick(movement: any, pickOptions?: PickOptions, pickConfig?: PickConfiguration): PickResult {
        // clean old picks
        Object.entries(this.drillPicksCache).forEach(([key, value]) => {
            if (value.validityTime <= new Date().getTime()) {
                delete this.drillPicksCache[key as unknown as PickOptions];
            }
        });

        let picks: any = [];
        switch (pickOptions) {
            case PickOptions.PICK_ONE:
            case PickOptions.PICK_ALL: {
                const cachedPicks = this.drillPicksCache[pickOptions];

                if (cachedPicks && cachedPicks.validityTime > new Date().getTime()) {
                    picks = cachedPicks.value;
                } else {
                    picks = this.scene.drillPick(
                        movement.endPosition,
                        pickConfig?.drillPickLimit,
                        pickConfig?.pickWidth,
                        pickConfig?.pickHeight
                    );
                    picks = picks.length === 0 ? null : picks;

                    this.drillPicksCache[pickOptions] = {
                        validityTime: new Date().getTime() + DRILL_PICK_THROTTLE_TIME,
                        value: picks,
                    };
                }
                break;
            }
            case PickOptions.PICK_FIRST: {
                const pick = this.scene.pick(movement.endPosition, pickConfig?.pickWidth, pickConfig?.pickHeight);
                picks = pick === undefined ? null : [pick];
                break;
            }
            case PickOptions.NO_PICK:
                break;
            default:
                break;
        }

        // Picks can be cesium entity or cesium primitive
        if (picks) {
            picks = picks.map((pick: any) => (pick.id && pick.id instanceof Entity ? pick.id : pick.primitive));
        }

        return { movement: movement, cesiumEntities: picks };
    }

    private addEntities(picksAndMovement: any, entityType: any, pickOption?: PickOptions, pickFilter?: (any: any) => boolean): EventResult {
        if (picksAndMovement.cesiumEntities === null) {
            picksAndMovement.entities = null;

            return picksAndMovement;
        }
        let entities: null | any[] = [];
        if (pickOption !== PickOptions.NO_PICK) {
            if (entityType) {
                entities = picksAndMovement.cesiumEntities.map((pick: any) => pick.acEntity).filter((acEntity: any) => acEntity);
            } else {
                entities = picksAndMovement.cesiumEntities.map((pick: any) => pick.acEntity);
            }

            entities = UtilsService.unique(entities as any[]);
            entities = pickFilter && entities ? entities.filter(pickFilter) : entities;
            if (entities.length === 0) {
                entities = null;
            }
        }

        picksAndMovement.entities = entities;

        return picksAndMovement;
    }
}
